// src/components/HomePage.jsx — Nuvport OS landing page
// Design language: Syne, #3ddc84 Signal Green, clip-path buttons,
// custom cursor, noise/scanlines, hero, ticker tape, pain points,
// world monitor, module grid, pricing, waitlist, footer.
import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";

// ── PALETTE ─────────────────────────────────────────────────────────────────
const BG     = "#0a0a0a";
const BG2    = "#111111";
const BG3    = "#0d1117";
const BORDER = "#1c2333";
const BDR2   = "#1a2030";
const GREEN  = "#3ddc84";
const GDIM   = "#2db870";   // dim Signal Green for gradients
const GDARK  = "#0E180F";   // dark Signal Green tint (= FAINT)
const RED    = "#ff6b35";   // WAS #FF4455
const TEXT   = "#c9d1d9";   // WAS #f0f0f0
const MUTED  = "#8b949e";   // WAS #888888
const FAINT  = "#0E180F";

const MONO    = "'IBM Plex Mono', 'IBM Plex Mono', 'Courier New', monospace";
const SANS    = "'DM Sans', 'DM Sans', system-ui, sans-serif";
const DISPLAY = "'Playfair Display', Georgia, serif";
const BG0     = "#060a0f";

// ── STATIC DATA (illustrative prices — for visual demo only) ─────────────────
const TICKER_ITEMS = [
  { sym: "AAPL",    price: "221.53",   chg: "+1.84",  pct: "+0.84%" },
  { sym: "BTC",     price: "87,240",   chg: "+1,120", pct: "+1.30%" },
  { sym: "SPX",     price: "5,611.85", chg: "+22.40", pct: "+0.40%" },
  { sym: "GOLD",    price: "3,022.10", chg: "+8.60",  pct: "+0.29%" },
  { sym: "EUR/USD", price: "1.0841",   chg: "-0.0018",pct: "-0.17%" },
  { sym: "TSLA",    price: "278.44",   chg: "+6.12",  pct: "+2.25%" },
  { sym: "ETH",     price: "2,018.55", chg: "-34.20", pct: "-1.67%" },
  { sym: "WTI",     price: "68.34",    chg: "-0.82",  pct: "-1.19%" },
  { sym: "NVDA",    price: "114.72",   chg: "+3.41",  pct: "+3.06%" },
  { sym: "GBP/USD", price: "1.2932",   chg: "+0.0024",pct: "+0.19%" },
];

const FEATURES = [
  { id: "01 / LIVE_DATA", title: "Live Market Data",          desc: "Real-time quotes across all 22 modules with 2-second polling. Prices, sparklines, and news via Yahoo Finance." },
  { id: "02 / ANALYTICS", title: "Portfolio Analytics",       desc: "Risk-return scatter, correlation matrix, sector exposure, factor decomposition — 8 charts in one Analytics module." },
  { id: "03 / GEO_RISK",  title: "Geopolitical Intelligence", desc: "40+ intelligence layers, 19 live conflict zones, oil pipelines, military deployments — portfolio pinned on the map." },
  { id: "04 / QUANT",     title: "Quantitative Tools",        desc: "DCF valuation, Monte Carlo simulations, backtesting, efficient frontier, and Sharpe optimiser — institutional tools at retail price." },
  { id: "05 / MACRO",     title: "Macro Command Center",      desc: "FED Monitor, Econ Statistics, World Econ Index — Fed policy, CPI, GDP, PMI, yield curve across 6 major economies." },
  { id: "06 / INTEL",     title: "Insider Intelligence",      desc: "500+ CEO, director, and hedge fund Form 4 filings. Earnings calendar. AI-scored news sentiment across 7 feeds." },
];

const ALL_MODULES = [
  // PORTFOLIO
  { name: "Dashboard",        desc: "Portfolio health at a glance",                     tier: "FREE", group: "PORTFOLIO"    },
  { name: "Builder",          desc: "Build and weight your holdings",                    tier: "FREE", group: "PORTFOLIO"    },
  { name: "Valuation",        desc: "Fundamental scores for 76 stocks",                  tier: "PRO",  group: "PORTFOLIO"    },
  { name: "Optimizer",        desc: "Mean-variance portfolio optimisation",              tier: "PRO",  group: "PORTFOLIO"    },
  // SIMULATION
  { name: "Backtest",         desc: "Historical performance vs S&P 500",                 tier: "PRO",  group: "SIMULATION"   },
  { name: "DCF Model",        desc: "Intrinsic value calculator",                        tier: "PRO",  group: "SIMULATION"   },
  { name: "Monte Carlo",      desc: "Probabilistic 10-year simulations",                 tier: "PRO",  group: "SIMULATION"   },
  { name: "Analytics",        desc: "Multi-chart portfolio visualisation",               tier: "PRO",  group: "SIMULATION"   },
  // MARKETS
  { name: "Watchlist",        desc: "Live prices, sparklines, company deep dive",        tier: "FREE", group: "MARKETS"      },
  { name: "Screener",         desc: "12-metric filter with 5 preset screens",            tier: "PRO",  group: "MARKETS"      },
  { name: "FX & Markets",     desc: "47 currency pairs, crypto, commodities",            tier: "PRO",  group: "MARKETS"      },
  { name: "Futures",          desc: "Live prices + portfolio correlation for 25 contracts", tier: "PRO",  group: "MARKETS"      },
  { name: "Fixed Income",     desc: "28-country bond rates, yield curve, spreads",       tier: "PRO",  group: "MARKETS"      },
  // MACRO
  { name: "Macro Dashboard",  desc: "Fed rates, CPI, GDP, yield curve, M2",              tier: "PRO",  group: "MACRO"        },
  { name: "FED Monitor",      desc: "Deep Fed policy data and recession signals",        tier: "PRO",  group: "MACRO"        },
  { name: "Econ Statistics",  desc: "Side-by-side macro: US, EU, UK, JP, DE, CA",        tier: "PRO",  group: "MACRO"        },
  { name: "World Econ Index", desc: "Global PMI and FRED charts for 10 countries",       tier: "PRO",  group: "MACRO"        },
  // GLOBAL
  { name: "Geo-Risk Map",     desc: "40+ layer geopolitical intelligence map",           tier: "PRO",  group: "GLOBAL"       },
  { name: "World Currencies", desc: "30+ live FX pairs across G10, Asia, EM",            tier: "PRO",  group: "GLOBAL"       },
  // INTELLIGENCE
  { name: "News Sentiment",   desc: "AI-scored news across 7 feeds",                     tier: "PRO",  group: "INTELLIGENCE" },
  { name: "Earnings",         desc: "Corporate earnings calendar and EPS estimates",     tier: "PRO",  group: "INTELLIGENCE" },
  { name: "Insider Intel",    desc: "500+ Form 4 SEC filings, CEO and hedge fund trades", tier: "PRO",  group: "INTELLIGENCE" },
];


// ── SECTION LABEL ─────────────────────────────────────────────────────────────
const SectionLabel = ({ text, color = '#666' }) => (
  <p style={{
    fontFamily: "'DM Sans', 'DM Sans', system-ui, sans-serif",
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color,
    margin: '0 0 16px',
  }}>
    {text}
  </p>
);

// ── COMPONENT ────────────────────────────────────────────────────────────────
const INTEREST_OPTIONS = [
  "I want to make smarter investment decisions",
  "I can't afford professional tools",
  "I'm just starting to learn about investing",
  "I want to understand how global events affect my portfolio",
  "Other",
];

function genReferralCode() {
  return "NV-" + Math.random().toString(36).slice(2, 8).toUpperCase();
}

export default function HomePage({ onLogin, onOpenTerms, onOpenPrivacy }) {
  const [cursor,        setCursor]        = useState({ x: -100, y: -100, big: false });
  const [deletedBanner, setDeletedBanner] = useState(false);

  // ── Waitlist state ─────────────────────────────────────────────────────────
  const [waitlistOpen,    setWaitlistOpen]    = useState(false);
  const [wlEmail,         setWlEmail]         = useState("");
  const [wlReason,        setWlReason]        = useState("");
  const [wlStatus,        setWlStatus]        = useState("idle"); // idle|loading|success|error|duplicate
  const [wlErrMsg,        setWlErrMsg]        = useState("");
  const [wlCount,         setWlCount]         = useState(null);
  const [myReferralCode,  setMyReferralCode]  = useState("");
  const [referredBy,      setReferredBy]      = useState("");

  // Show confirmation banner if redirected here after account deletion
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('deleted') === 'true') {
      setDeletedBanner(true);
      window.history.replaceState({}, '', '/');
      setTimeout(() => setDeletedBanner(false), 6000);
    }
    // Capture referral code from URL
    const ref = params.get('ref');
    if (ref) setReferredBy(ref);
  }, []);

  // Fetch live waitlist count on mount
  useEffect(() => {
    if (!supabase) return;
    supabase.rpc("get_waitlist_count")
      .then(({ data }) => { if (data != null) setWlCount(data); })
      .catch(() => {});
  }, []);

  async function handleWaitlistSubmit(e) {
    e.preventDefault();
    if (!wlEmail.trim() || !wlReason || wlStatus === "loading") return;
    setWlStatus("loading");
    setWlErrMsg("");

    const code = genReferralCode();

    if (!supabase) {
      // No Supabase configured — just show success
      setMyReferralCode(code);
      setWlStatus("success");
      setWlCount(c => (c ?? 0) + 1);
      return;
    }

    const { error } = await supabase.from("waitlist").insert({
      email:           wlEmail.trim().toLowerCase(),
      interest_reason: wlReason,
      referral_code:   code,
      referred_by:     referredBy || null,
    });

    if (error) {
      if (error.code === "23505") {
        // Unique violation — email already on list
        setWlStatus("duplicate");
      } else {
        setWlStatus("error");
        setWlErrMsg("Something went wrong. Please try again.");
      }
      return;
    }

    setMyReferralCode(code);
    setWlStatus("success");
    setWlCount(c => (c ?? 0) + 1);
  }

  // Load Syne + IBM Plex Sans + JetBrains Mono
  useEffect(() => {
    if (document.getElementById("hp-google-fonts")) return;
    const link = document.createElement("link");
    link.id   = "hp-google-fonts";
    link.rel  = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,800;1,400&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=IBM+Plex+Mono:wght@400;500;600&display=swap";
    document.head.appendChild(link);
  }, []);

  // Scroll-triggered fadeInUp animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("hp-visible"); }),
      { threshold: 0.12 }
    );
    document.querySelectorAll(".hp-fade").forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Custom cursor tracking
  useEffect(() => {
    const onMove = (e) => setCursor(c => ({ ...c, x: e.clientX, y: e.clientY }));
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const makeBig   = useCallback(() => setCursor(c => ({ ...c, big: true  })), []);
  const makeSmall = useCallback(() => setCursor(c => ({ ...c, big: false })), []);

  const CSS = `
    @keyframes hp-fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
    .hp-fade { opacity:0;transform:translateY(16px); }
    .hp-fade.hp-visible { animation:hp-fadeUp .55s ease both; }
    @keyframes hp-ticker   { from{transform:translateX(0)} to{transform:translateX(-50%)} }
    @keyframes hp-slideIn  { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:none} }
    @keyframes hp-ringPulse{ 0%,100%{opacity:.2;transform:translate(-50%,-50%) scale(1)} 50%{opacity:.5;transform:translate(-50%,-50%) scale(1.015)} }
    @keyframes hp-blink    { 0%,100%{opacity:1} 50%{opacity:0} }
    @keyframes hp-zonePulse { 0%,100%{transform:scale(1);opacity:.9} 50%{transform:scale(1.5);opacity:.4} }
    @keyframes hp-ringExpand { 0%{transform:translate(-50%,-50%) scale(0.4);opacity:.8} 100%{transform:translate(-50%,-50%) scale(2.4);opacity:0} }
    @keyframes hp-scanMove  { 0%{top:0} 100%{top:100%} }
    .hp-zone-dot { position:absolute;width:8px;height:8px;border-radius:50%;background:${GREEN};
      box-shadow:0 0 12px ${GREEN},0 0 24px rgba(61,220,132,.4);
      animation:hp-zonePulse 2s ease-in-out infinite;transform:translate(-50%,-50%);z-index:3; }
    .hp-zone-dot::before { content:'';position:absolute;top:50%;left:50%;width:100%;height:100%;
      border-radius:50%;border:1px solid ${GREEN};
      animation:hp-ringExpand 2s ease-out infinite; }
    .hp-zone-dot.critical { background:#ff6b35;box-shadow:0 0 12px #ff6b35,0 0 24px rgba(255,107,53,.4); }
    .hp-zone-dot.critical::before { border-color:#ff6b35; }
    .hp-world-grid { display:grid;grid-template-columns:1fr 1fr;gap:64px;align-items:center;max-width:1100px;margin:0 auto; }
    @media(max-width:900px) {
      .hp-world-grid { grid-template-columns:1fr!important;gap:40px!important; }
      .hp-pain-grid  { grid-template-columns:1fr!important; }
    }
    .hp-scanlines { position:fixed;inset:0;z-index:1000;pointer-events:none;
      background:repeating-linear-gradient(to bottom,transparent 0,transparent 2px,rgba(0,0,0,.07) 2px,rgba(0,0,0,.07) 4px); }
    .hp-noise { position:fixed;inset:0;z-index:999;pointer-events:none;opacity:.025;
      background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
      background-size:256px; }
    .hp-feat-card { padding:32px 28px;position:relative;overflow:hidden;transition:background .2s;cursor:default; }
    .hp-feat-card::before { content:'';position:absolute;top:0;left:0;right:0;height:2px;
      background:linear-gradient(90deg,transparent,#333);transform:scaleX(0);transform-origin:left;transition:transform .35s; }
    .hp-feat-card:hover { background:${FAINT}; }
    .hp-feat-card:hover::before { transform:scaleX(1); }
    .hp-stat-box { position:relative;overflow:hidden; }
    .hp-stat-box::after { content:'';position:absolute;bottom:0;left:0;right:0;height:1px;
      background:linear-gradient(90deg,transparent,${GREEN},transparent);transform:scaleX(0);transition:transform .4s; }
    .hp-stat-box:hover::after { transform:scaleX(1); }
    .hp-mod-tag { font-family:${MONO};font-size:9px;letter-spacing:.14em;text-transform:uppercase;
      border:1px solid ${BDR2};color:${MUTED};padding:7px 14px;background:transparent;
      clip-path:polygon(0 0,calc(100% - 6px) 0,100% 6px,100% 100%,6px 100%,0 calc(100% - 6px));
      transition:all .15s;cursor:none; }
    .hp-mod-tag:hover, .hp-mod-tag.on {
      border-color:${GDIM}!important;color:${GREEN}!important;
      background:rgba(61,220,132,.04)!important;box-shadow:0 0 12px rgba(61,220,132,.08)!important; }
    .hp-btn-primary { font-family:${DISPLAY};font-size:14px;letter-spacing:.14em;color:${BG};
      background:${GREEN};border:none;padding:13px 40px;cursor:none;
      clip-path:polygon(0 0,calc(100% - 10px) 0,100% 10px,100% 100%,10px 100%,0 calc(100% - 10px));
      transition:background .15s,box-shadow .15s; }
    .hp-btn-primary:hover { background:${GDIM};box-shadow:0 0 40px rgba(61,220,132,.35); }
    .hp-btn-ghost { font-family:${DISPLAY};font-size:13px;letter-spacing:.14em;color:${MUTED};
      background:transparent;border:1px solid ${BDR2};padding:12px 0;width:100%;cursor:none;
      clip-path:polygon(0 0,calc(100% - 8px) 0,100% 8px,100% 100%,8px 100%,0 calc(100% - 8px));
      transition:border-color .15s,color .15s; }
    .hp-btn-ghost:hover { border-color:${MUTED};color:${TEXT}; }
    .hp-btn-secondary { font-family:${DISPLAY};font-size:14px;letter-spacing:.14em;color:#fff;
      background:transparent;border:1px solid #555;padding:13px 40px;cursor:none;
      clip-path:polygon(0 0,calc(100% - 10px) 0,100% 10px,100% 100%,10px 100%,0 calc(100% - 10px));
      transition:background .15s,box-shadow .15s,color .15s;white-space:nowrap; }
    .hp-btn-secondary:hover { border-color:#888;color:#fff; }
    .hp-hero-ctas { display:flex;gap:16px;align-items:center;justify-content:center;flex-wrap:wrap; }
    .hp-social-proof { display:flex;align-items:center;justify-content:center;gap:0;flex-wrap:wrap; }
    .hp-social-proof-dot { width:4px;height:4px;border-radius:50%;background:${MUTED};margin:0 14px;flex-shrink:0; }
    @media(max-width:600px) {
      .hp-hero-ctas { flex-direction:column;width:100%; }
      .hp-btn-primary, .hp-btn-secondary { width:100%;padding:13px 0;text-align:center; }
    }
    .hp-blink { animation:hp-blink 1.4s ease infinite; }
    .hp-fl-link { color:${MUTED};text-decoration:none;transition:color .15s; }
    .hp-fl-link:hover { color:${TEXT}; }
    @media(max-width:1024px) {
      .hp-mod-grid    { grid-template-columns:repeat(2,1fr)!important; }
    }
    @media(max-width:900px) {
      .hp-feat-grid   { grid-template-columns:1fr!important; }
      .hp-feat-card   { border-right:none!important; }
      .hp-stats-grid  { grid-template-columns:1fr 1fr!important; }
      .hp-feat-header { grid-template-columns:1fr!important; }
      .hp-hero-inner  { padding:60px 28px!important; }
      .hp-plans-grid  { grid-template-columns:1fr!important; }
      .hp-pain-grid   { grid-template-columns:1fr!important; }
    }
    @media(max-width:480px) {
      .hp-mod-grid    { grid-template-columns:1fr!important; }
    }
    @media(max-width:768px) {
      .hp-footer-main   { grid-template-columns:1fr!important;gap:24px!important;padding:32px 24px!important; }
      .hp-footer-bottom { flex-direction:column;align-items:flex-start!important;padding:16px 24px!important; }
    }
  `;

  return (
    <div style={{ background: BG, color: TEXT, fontFamily: MONO, fontSize: 13, lineHeight: 1.6, overflowX: "hidden", cursor: "none", position: "relative" }}>
      <style>{CSS}</style>

      {/* Account deleted banner */}
      {deletedBanner && (
        <div style={{
          position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999,
          background: '#0d1520', border: '1px solid #3ddc84',
          borderRadius: 8, padding: '12px 24px',
          fontFamily: 'IBM Plex Mono', fontSize: 12, color: '#3ddc84',
          boxShadow: '0 0 24px rgba(61,220,132,0.2)',
          whiteSpace: 'nowrap',
        }}>
          ✓ Your account has been permanently deleted.
        </div>
      )}

      {/* Overlays */}
      <div className="hp-scanlines" />
      <div className="hp-noise" />

      {/* Custom cursor */}
      <div style={{
        position: "fixed", zIndex: 9999, pointerEvents: "none",
        left: cursor.x, top: cursor.y,
        width:  cursor.big ? 28 : 4,
        height: cursor.big ? 28 : 18,
        borderRadius: cursor.big ? "50%" : 0,
        background:   cursor.big ? "transparent" : GREEN,
        border:       cursor.big ? `1px solid ${GREEN}` : "none",
        transform: "translate(-50%,-50%)",
        boxShadow: cursor.big
          ? "0 0 24px rgba(61,220,132,.25), inset 0 0 10px rgba(61,220,132,.05)"
          : `0 0 14px ${GREEN}`,
        transition: "width .2s, height .2s, border-radius .2s",
      }} />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section style={{ minHeight: "92vh", display: "flex", flexDirection: "column", position: "relative" }}>
        {/* Grid background */}
        <div style={{
          position: "absolute", inset: 0, zIndex: 0,
          backgroundImage: `linear-gradient(${BORDER} 1px,transparent 1px),linear-gradient(90deg,${BORDER} 1px,transparent 1px)`,
          backgroundSize: "60px 60px",
          WebkitMaskImage: "radial-gradient(ellipse 70% 70% at 50% 50%,black 0%,transparent 70%)",
          maskImage: "radial-gradient(ellipse 70% 70% at 50% 50%,black 0%,transparent 70%)",
          opacity: 0.7,
        }} />

        {/* Centered content */}
        <div className="hp-hero-inner" style={{
          flex: 1,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          textAlign: "center",
          padding: "56px 60px",
          borderBottom: `1px solid ${BDR2}`,
          position: "relative", zIndex: 1,
        }}>
          {/* Green ambient glow */}
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 700, height: 500, background: "radial-gradient(ellipse,rgba(61,220,132,.06) 0%,transparent 70%)", pointerEvents: "none" }} />

          {/* Eyebrow */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase", color: MUTED, marginBottom: 36, animation: "hp-slideIn .5s ease both", position: "relative", zIndex: 1 }}>
            <span style={{ width: 36, height: 1, background: MUTED, display: "inline-block", flexShrink: 0 }} />
            22 modules · $29/month
            <span style={{ width: 36, height: 1, background: MUTED, display: "inline-block", flexShrink: 0 }} />
          </div>

          {/* Big headline */}
          <h1 style={{ fontFamily: DISPLAY, fontSize: "clamp(44px,8vw,96px)", fontWeight: 400, lineHeight: 1.0, letterSpacing: ".03em", color: "#fff", margin: "0 0 28px", animation: "hp-slideIn .5s .08s ease both", position: "relative", zIndex: 1, maxWidth: 760 }}>
            Nuvport<span style={{ color: GREEN, textShadow: "0 0 60px rgba(61,220,132,.4)" }}>_OS</span>
          </h1>

          <p style={{ fontFamily: SANS, fontSize: 16, fontWeight: 300, color: MUTED, lineHeight: 1.85, maxWidth: 560, marginBottom: 48, animation: "hp-slideIn .5s .16s ease both", position: "relative", zIndex: 1 }}>
            <span style={{ color: TEXT }}>22 institutional-grade modules.</span>{" "}
            Real-time geo-risk intelligence. Portfolio analytics built for serious retail investors — not Wall Street firms.
          </p>

          {/* Dual CTAs */}
          <div className="hp-hero-ctas" style={{ animation: "hp-slideIn .5s .24s ease both", position: "relative", zIndex: 1, marginBottom: 36 }}>
            <button className="hp-btn-primary" onClick={onLogin} onMouseEnter={makeBig} onMouseLeave={makeSmall}>
              Start Free
            </button>
            <button
              className="hp-btn-secondary"
              onClick={() => document.getElementById("hp-pricing")?.scrollIntoView({ behavior: "smooth" })}
              onMouseEnter={makeBig}
              onMouseLeave={makeSmall}
            >
              Start Pro — No credit card required →
            </button>
          </div>

          {/* Social proof line */}
          <div className="hp-social-proof" style={{ animation: "hp-slideIn .5s .32s ease both", position: "relative", zIndex: 1 }}>
            {["22 modules", "40+ intelligence layers", "19 live conflict zones", "$29/month"].map((item, i, arr) => (
              <span key={item} style={{ display: "flex", alignItems: "center" }}>
                <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: ".1em", color: MUTED }}>{item}</span>
                {i < arr.length - 1 && <span className="hp-social-proof-dot" />}
              </span>
            ))}
          </div>
        </div>

        {/* Ticker tape — prices are illustrative static values for visual demo only */}
        <div style={{
          borderTop: `1px solid ${BDR2}`,
          background: BG2,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          height: 44,
          position: "relative",
          zIndex: 1,
        }}>
          <span style={{ position: "absolute", right: 12, zIndex: 2, fontSize: 9, color: MUTED, letterSpacing: ".06em", background: BG2, padding: "0 6px", pointerEvents: "none" }}>ILLUSTRATIVE</span>
          <div style={{
            display: "flex",
            gap: 0,
            animation: "hp-ticker 55s linear infinite",
            whiteSpace: "nowrap",
            willChange: "transform",
          }}>
            {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => {
              const positive = !item.chg.startsWith("-");
              const color = positive ? GREEN : RED;
              return (
                <span key={i} style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "0 28px",
                  borderRight: `1px solid ${BDR2}`,
                  flexShrink: 0,
                  height: 44,
                }}>
                  <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: ".12em", color: TEXT, fontWeight: 600 }}>
                    {item.sym}
                  </span>
                  <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: ".06em", color: TEXT }}>
                    {item.price}
                  </span>
                  <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: ".04em", color, display: "inline-flex", alignItems: "center", gap: 3 }}>
                    <span style={{ fontSize: 8 }}>{positive ? "▲" : "▼"}</span>
                    {item.pct}
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── STATS ────────────────────────────────────────────────────────── */}
      <div className="hp-stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", background: BG, borderTop: `1px solid ${BDR2}`, borderBottom: `1px solid ${BDR2}`, position: "relative", zIndex: 1 }}>
        {[
          { label: "Modules Active",    render: () => <span style={{ color: "#ffffff" }}>22</span> },
          { label: "Data Refresh Rate", render: () => <span style={{ color: "#ffffff" }}>2S</span> },
          { label: "Tickers Indexed",   render: () => <span style={{ color: "#ffffff" }}>76</span> },
          { label: "Panels Open",       render: () => <span style={{ color: "#ffffff" }}>∞</span> },
        ].map(({ label, render }, i) => (
          <div key={label} className="hp-stat-box" style={{ padding: "44px 36px", borderRight: i < 3 ? `1px solid ${BDR2}` : "none" }}>
            <div style={{ fontFamily: DISPLAY, fontSize: 56, lineHeight: 1, color: "#fff", marginBottom: 8, letterSpacing: ".02em" }}>{render()}</div>
            <div style={{ fontSize: 9, letterSpacing: ".18em", textTransform: "uppercase", color: MUTED }}>{label}</div>
          </div>
        ))}
      </div>

      {/* ── PAIN POINTS ──────────────────────────────────────────────────── */}
      <section className="hp-fade" style={{ background: BG2, borderTop: `1px solid ${BDR2}`, borderBottom: `1px solid ${BDR2}`, position: "relative", zIndex: 1, padding: "80px 40px" }}>
        {/* Section headline */}
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <SectionLabel text="The Problem" />
          <h2 style={{ fontFamily: DISPLAY, fontSize: "clamp(28px,5vw,52px)", fontWeight: 400, letterSpacing: ".03em", color: "#fff", lineHeight: 1.1, margin: 0 }}>
            Professional tools were built for<br />
            institutions. <span style={{ color: MUTED }}>Not for you.</span>
          </h2>
        </div>

        {/* Three pain point cards */}
        <div className="hp-pain-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 1, background: BDR2, border: `1px solid ${BDR2}`, maxWidth: 1000, margin: "0 auto 56px" }}>
          {[
            {
              cost: "$24,000/year",
              name: "Bloomberg Terminal",
              body: "Used by 325,000 professionals. Unaffordable for the other 100 million retail investors.",
            },
            {
              cost: "$12,000/year",
              name: "FactSet",
              body: "Enterprise-grade data locked behind enterprise-grade pricing. Not built for individual investors.",
            },
            {
              cost: "6 different tools",
              name: "Cobbling it together",
              body: "TradingView for charts. FRED for macro. Reddit for news. And still missing geopolitical risk, insider filings, and an earnings calendar.",
            },
          ].map((card, i) => (
            <div key={i} style={{ background: BG2, padding: "36px 32px", position: "relative", overflow: "hidden" }}>
              {/* Faint watermark number */}
              <div style={{ fontFamily: DISPLAY, fontSize: 100, lineHeight: 1, color: BDR2, position: "absolute", right: -8, bottom: -8, pointerEvents: "none", userSelect: "none", zIndex: 0 }}>
                {i + 1}
              </div>
              <div style={{ position: "relative", zIndex: 1 }}>
                <div style={{ fontFamily: DISPLAY, fontSize: "clamp(22px,3vw,36px)", fontWeight: 400, color: RED, letterSpacing: ".02em", marginBottom: 8, lineHeight: 1 }}>
                  {card.cost}
                </div>
                <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: ".16em", textTransform: "uppercase", color: MUTED, marginBottom: 16 }}>
                  {card.name}
                </div>
                <div style={{ fontFamily: SANS, fontSize: 13, color: MUTED, lineHeight: 1.8, fontWeight: 300 }}>
                  {card.body}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Divider + resolution */}
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 1, height: 40, background: `linear-gradient(transparent,${GREEN})`, margin: "0 auto 24px" }} />
          <div style={{ fontFamily: DISPLAY, fontSize: "clamp(28px,5vw,56px)", fontWeight: 400, color: "#ffffff", letterSpacing: ".03em", lineHeight: 1.1 }}>
            Nuvport OS gives you all 22 modules.<br />For $29.
          </div>
        </div>
      </section>

      {/* ── GEO-RISK MAP SHOWCASE ────────────────────────────────────────── */}
      <section className="hp-fade" style={{ background: BG2, borderTop: `1px solid ${BDR2}`, borderBottom: `1px solid ${BDR2}`, position: "relative", zIndex: 1, padding: "96px 40px", overflow: "hidden" }}>
        {/* Faint radial glow behind the whole section */}
        <div style={{ position: "absolute", top: "50%", right: 0, transform: "translateY(-50%)", width: 600, height: 600, background: "radial-gradient(ellipse,rgba(61,220,132,.04) 0%,transparent 70%)", pointerEvents: "none" }} />

        {/* Section headline — centered above */}
        <div style={{ textAlign: "center", marginBottom: 72 }}>
          <SectionLabel text="Geo-Risk Map · Pro Feature" />
          <h2 style={{ fontFamily: DISPLAY, fontSize: "clamp(26px,5vw,48px)", fontWeight: 400, letterSpacing: ".03em", color: "#fff", lineHeight: 1.1, margin: 0 }}>
            The only retail terminal with live<br />
            <span style={{ color: "#ffffff" }}>geopolitical intelligence.</span>
          </h2>
        </div>

        <div className="hp-world-grid">
          {/* LEFT — copy + feature list */}
          <div>
            <p style={{ fontFamily: SANS, fontSize: 15, fontWeight: 300, color: MUTED, lineHeight: 1.9, marginBottom: 40 }}>
              While Bloomberg users pay{" "}
              <span style={{ color: TEXT }}>$2,000/month</span> for geo-risk data,
              Nuvport OS gives you{" "}
              <span style={{ color: TEXT }}>40+ intelligence layers</span>,{" "}
              <span style={{ color: TEXT }}>19 active conflict zones</span>, and your
              portfolio pinned directly on the map — for{" "}
              <span style={{ color: TEXT, fontWeight: 500 }}>$29/month</span>.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {[
                "40+ toggleable intelligence layers",
                "19 live conflict zones with risk ratings",
                "Portfolio holdings pinned directly on the map",
                "Oil pipelines, shipping lanes, naval deployments",
                "Sanctions tracker and cyber incident overlay",
                "Integrated news feed per conflict zone",
                "Real-time geo-risk alerts on your Dashboard",
                "Military movements, election risk, protest activity",
              ].map(feat => (
                <div key={feat} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <span style={{ color: "#ffffff", fontFamily: MONO, fontSize: 13, lineHeight: 1.6, flexShrink: 0 }}>✓</span>
                  <span style={{ fontFamily: SANS, fontSize: 13, color: TEXT, lineHeight: 1.7, fontWeight: 300 }}>{feat}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — terminal map mockup */}
          <div style={{ position: "relative" }}>
            {/* Terminal frame */}
            <div style={{
              background: BG0,
              border: `1px solid ${GREEN}`,
              boxShadow: `0 0 40px rgba(61,220,132,.12), inset 0 0 40px rgba(0,0,0,.5)`,
              position: "relative",
              overflow: "hidden",
            }}>
              {/* Title bar */}
              <div style={{ background: "#0d1117", borderBottom: `1px solid ${BDR2}`, padding: "8px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="hp-blink" style={{ width: 6, height: 6, borderRadius: "50%", background: GREEN, display: "inline-block", boxShadow: `0 0 6px ${GREEN}` }} />
                  <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: ".14em", color: GREEN }}>GEO_RISK_MAP · LIVE</span>
                </div>
                <span style={{ fontFamily: MONO, fontSize: 9, color: MUTED, letterSpacing: ".08em" }}>19 ZONES ACTIVE</span>
              </div>

              {/* Map area */}
              <div style={{ position: "relative", padding: "24px", height: 320 }}>
                {/* Scanline overlay */}
                <div style={{ position: "absolute", inset: 0, zIndex: 4, pointerEvents: "none", background: "repeating-linear-gradient(to bottom,transparent 0,transparent 3px,rgba(0,0,0,.08) 3px,rgba(0,0,0,.08) 4px)" }} />

                {/* Simplified SVG world map */}
                <svg viewBox="0 0 800 400" style={{ width: "100%", height: "100%", position: "absolute", inset: 0, padding: "20px 24px", boxSizing: "border-box" }} preserveAspectRatio="xMidYMid meet">
                  <defs>
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="2" result="blur" />
                      <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                  </defs>
                  {/* North America */}
                  <path d="M 60 60 L 200 55 L 210 120 L 190 170 L 150 200 L 120 210 L 90 190 L 55 160 L 45 110 Z" fill="#1c2333" stroke="#2d3748" strokeWidth="1" />
                  {/* South America */}
                  <path d="M 150 230 L 195 225 L 215 290 L 200 360 L 165 370 L 140 330 L 130 270 Z" fill="#1c2333" stroke="#2d3748" strokeWidth="1" />
                  {/* Europe */}
                  <path d="M 370 50 L 440 45 L 455 90 L 430 115 L 390 120 L 360 100 L 355 70 Z" fill="#1c2333" stroke="#2d3748" strokeWidth="1" />
                  {/* Africa */}
                  <path d="M 380 130 L 440 125 L 465 180 L 460 270 L 420 310 L 385 300 L 360 240 L 355 170 Z" fill="#1c2333" stroke="#2d3748" strokeWidth="1" />
                  {/* Russia / Central Asia */}
                  <path d="M 450 30 L 680 25 L 690 90 L 600 110 L 500 105 L 455 80 Z" fill="#1c2333" stroke="#2d3748" strokeWidth="1" />
                  {/* Middle East */}
                  <path d="M 455 115 L 510 110 L 530 155 L 510 185 L 465 180 L 450 150 Z" fill="#1c2333" stroke="#2d3748" strokeWidth="1" />
                  {/* South/Southeast Asia */}
                  <path d="M 540 100 L 680 95 L 700 160 L 660 190 L 580 185 L 535 150 Z" fill="#1c2333" stroke="#2d3748" strokeWidth="1" />
                  {/* Australia */}
                  <path d="M 620 250 L 730 245 L 745 315 L 700 345 L 640 340 L 610 300 Z" fill="#1c2333" stroke="#2d3748" strokeWidth="1" />
                  {/* Grid lines */}
                  {[100, 200, 300, 400, 500, 600, 700].map(x => (
                    <line key={`v${x}`} x1={x} y1={0} x2={x} y2={400} stroke="#1a1f2e" strokeWidth="0.5" />
                  ))}
                  {[80, 160, 240, 320].map(y => (
                    <line key={`h${y}`} x1={0} y1={y} x2={800} y2={y} stroke="#1a1f2e" strokeWidth="0.5" />
                  ))}
                  {/* Conflict zone rings — Ukraine ~(430, 80) */}
                  <circle cx="432" cy="78" r="18" fill="none" stroke="#ff6b35" strokeWidth="1" opacity="0.4" />
                  <circle cx="432" cy="78" r="28" fill="none" stroke="#ff6b35" strokeWidth="0.5" opacity="0.2" />
                  {/* Conflict zone rings — Middle East ~(490, 148) */}
                  <circle cx="490" cy="148" r="20" fill="none" stroke="#ff6b35" strokeWidth="1" opacity="0.4" />
                  <circle cx="490" cy="148" r="32" fill="none" stroke="#ff6b35" strokeWidth="0.5" opacity="0.2" />
                  {/* Conflict zone rings — Taiwan ~(672, 142) */}
                  <circle cx="672" cy="142" r="14" fill="none" stroke="#ffd700" strokeWidth="1" opacity="0.5" />
                  <circle cx="672" cy="142" r="22" fill="none" stroke="#ffd700" strokeWidth="0.5" opacity="0.2" />
                  {/* Labels */}
                  <text x="432" y="108" textAnchor="middle" fill="#ff6b35" fontSize="9" fontFamily="monospace" letterSpacing="1" opacity="0.9">UKRAINE</text>
                  <text x="490" y="180" textAnchor="middle" fill="#ff6b35" fontSize="9" fontFamily="monospace" letterSpacing="1" opacity="0.9">MIDDLE EAST</text>
                  <text x="672" y="128" textAnchor="middle" fill="#ffd700" fontSize="9" fontFamily="monospace" letterSpacing="1" opacity="0.9">TAIWAN STR.</text>
                </svg>

                {/* Animated dots positioned over SVG */}
                <div style={{ position: "absolute", inset: 0, padding: "20px 24px", boxSizing: "border-box", pointerEvents: "none" }}>
                  {/* Ukraine: ~54%, 20% within map area */}
                  <div className="hp-zone-dot critical" style={{ left: "54%", top: "20%", animationDelay: "0s" }} />
                  {/* Middle East: ~61%, 37% */}
                  <div className="hp-zone-dot critical" style={{ left: "61%", top: "37%", animationDelay: "0.7s" }} />
                  {/* Taiwan: ~84%, 36% */}
                  <div className="hp-zone-dot" style={{ left: "84%", top: "36%", animationDelay: "1.4s", background: "#ffd700", boxShadow: "0 0 12px #ffd700, 0 0 24px rgba(255,215,0,.4)" }} />
                </div>
              </div>

              {/* Bottom status bar */}
              <div style={{ background: "#0d1117", borderTop: `1px solid ${BDR2}`, padding: "8px 16px", display: "flex", gap: 24, alignItems: "center" }}>
                {[
                  { label: "CRITICAL", count: "3", color: "#ff6b35" },
                  { label: "ELEVATED", count: "8", color: "#ffd700" },
                  { label: "MONITORED", count: "8", color: GREEN },
                ].map(z => (
                  <div key={z.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: z.color, boxShadow: `0 0 6px ${z.color}`, display: "inline-block", flexShrink: 0 }} />
                    <span style={{ fontFamily: MONO, fontSize: 9, color: MUTED, letterSpacing: ".1em" }}>{z.label}</span>
                    <span style={{ fontFamily: MONO, fontSize: 9, color: z.color, fontWeight: 600 }}>{z.count}</span>
                  </div>
                ))}
                <div style={{ marginLeft: "auto", fontFamily: MONO, fontSize: 9, color: MUTED, letterSpacing: ".08em" }}>
                  UPD 00:02:14 AGO
                </div>
              </div>
            </div>

            {/* Pro badge — placed above the terminal frame, within section padding */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
              <div style={{ background: "rgba(61,220,132,.08)", border: `1px solid ${GREEN}44`, padding: "3px 12px", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span className="hp-blink" style={{ width: 4, height: 4, borderRadius: "50%", background: GREEN, display: "inline-block" }} />
                <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: ".14em", color: GREEN, fontWeight: 700 }}>PRO FEATURE</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────────────────── */}
      <section id="hero-features" className="hp-fade" style={{ background: BG, borderTop: `1px solid ${BDR2}`, borderBottom: `1px solid ${BDR2}`, position: "relative", zIndex: 1 }}>
        <div className="hp-feat-header" style={{ display: "grid", gridTemplateColumns: "260px 1fr", borderBottom: `1px solid ${BDR2}`, borderTop: `1px solid ${BDR2}` }}>
          <div style={{ padding: "48px 24px", borderRight: `1px solid ${BDR2}`, position: "relative" }}>
            <div style={{ fontFamily: DISPLAY, fontSize: 140, lineHeight: 1, color: FAINT, position: "absolute", right: -10, bottom: -10, pointerEvents: "none", userSelect: "none" }}>21</div>
            <SectionLabel text="22 Modules" color="#666" />
            <div style={{ fontFamily: DISPLAY, fontSize: "clamp(32px,4vw,48px)", color: "#fff", lineHeight: 1.0, letterSpacing: ".04em", position: "relative", zIndex: 1 }}>
              Everything<br />you need.
            </div>
          </div>
          <div style={{ padding: "48px 48px", display: "flex", alignItems: "center" }}>
            <p style={{ fontFamily: SANS, fontSize: 20, fontWeight: 300, color: MUTED, lineHeight: 1.9, maxWidth: 400 }}>
              From live market data to quantitative models — Nuvport OS brings professional-grade tools
              into one terminal workspace, built for serious retail investors.
            </p>
          </div>
        </div>
        <div className="hp-feat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", borderBottom: `1px solid ${BDR2}` }}>
          {FEATURES.map((f, i) => (
            <div key={f.id} className="hp-feat-card" style={{ borderRight: (i + 1) % 3 !== 0 ? `1px solid ${BDR2}` : "none" }}>
              <div style={{ fontSize: 9, letterSpacing: ".16em", textTransform: "uppercase", color: MUTED, marginBottom: 14 }}>{f.id}</div>
              <div style={{ fontFamily: DISPLAY, fontSize: 20, letterSpacing: ".06em", color: "#fff", marginBottom: 10 }}>{f.title}</div>
              <div style={{ fontFamily: SANS, fontSize: 12, color: MUTED, lineHeight: 1.8, fontWeight: 300 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── MODULES ──────────────────────────────────────────────────────── */}
      <section id="hp-modules" className="hp-fade" style={{ background: BG, borderTop: `1px solid ${BDR2}`, borderBottom: `1px solid ${BDR2}`, padding: "80px 40px", position: "relative", zIndex: 1 }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <SectionLabel text="22 Modules" />
          <h2 style={{ fontFamily: DISPLAY, fontSize: "clamp(28px,5vw,52px)", fontWeight: 400, letterSpacing: ".03em", color: "#fff", lineHeight: 1.1, margin: "0 0 14px" }}>
            Everything in one terminal.
          </h2>
          <p style={{ fontFamily: SANS, fontSize: 14, fontWeight: 300, color: MUTED, margin: 0 }}>
            22 interconnected modules. Every tool feeds every other.
          </p>
        </div>

        {/* 4-col grid with group dividers */}
        <div className="hp-mod-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 1, background: BDR2, border: `1px solid ${BDR2}`, maxWidth: 1100, margin: "0 auto" }}>
          {(() => {
            const COLS = 4;
            const rows = [];
            let lastGroup = null;
            let colsUsed = 0; // track columns used in current row

            ALL_MODULES.forEach((mod, idx) => {
              if (mod.group !== lastGroup) {
                // Fill any remaining ghost cells in the previous group's last row
                if (colsUsed > 0 && colsUsed < COLS) {
                  for (let f = 0; f < COLS - colsUsed; f++) {
                    rows.push(<div key={`fill-${lastGroup}-${f}`} style={{ background: BG2 }} />);
                  }
                }
                lastGroup = mod.group;
                colsUsed = 0;
                rows.push(
                  <div key={`group-${mod.group}`} style={{
                    gridColumn: "1 / -1",
                    background: BG,
                    padding: "8px 20px",
                    fontFamily: MONO,
                    fontSize: 9,
                    letterSpacing: ".2em",
                    textTransform: "uppercase",
                    color: "#555",
                    borderTop: rows.length > 0 ? `1px solid ${BDR2}` : "none",
                  }}>
                    {mod.group}
                  </div>
                );
              }

              const isHero = mod.name === "Geo-Risk Map" || mod.name === "Backtest";
              const span = isHero ? 2 : 1;

              // If adding this item would overflow the row, fill ghost cells first
              if (colsUsed + span > COLS) {
                const fill = COLS - colsUsed;
                for (let f = 0; f < fill; f++) {
                  rows.push(<div key={`fill-overflow-${idx}-${f}`} style={{ background: BG2 }} />);
                }
                colsUsed = 0;
              }

              rows.push(
                <div key={mod.name} style={{
                  gridColumn: isHero ? 'span 2' : 'span 1',
                  background: isHero ? GDARK : BG2,
                  border: isHero ? `1px solid ${BDR2}` : 'none',
                  padding: isHero ? '36px 28px' : '24px 20px',
                  position: 'relative', transition: 'background .2s'
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = isHero ? '#0d2018' : FAINT; makeBig(); }}
                  onMouseLeave={e => { e.currentTarget.style.background = isHero ? GDARK : BG2; makeSmall(); }}
                >
                  {/* Tier badge */}
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    padding: "2px 8px", marginBottom: 12,
                    background: mod.tier === "PRO" ? "rgba(61,220,132,.06)" : "transparent",
                    border: `1px solid ${mod.tier === "PRO" ? GREEN + "44" : BDR2}`,
                    fontSize: 8, letterSpacing: ".14em", fontFamily: MONO,
                    color: mod.tier === "PRO" ? GREEN : MUTED,
                  }}>
                    {mod.tier}
                  </div>
                  {/* Module name */}
                  <div style={{ fontFamily: MONO, fontSize: 11, letterSpacing: ".08em", color: "#ffffff", marginBottom: 8, fontWeight: 600 }}>
                    {mod.name}
                  </div>
                  {/* Description */}
                  <div style={{ fontFamily: SANS, fontSize: 12, color: MUTED, lineHeight: 1.6, fontWeight: 300 }}>
                    {mod.desc}
                  </div>
                </div>
              );

              colsUsed = (colsUsed + span) % COLS;
            });

            // Fill trailing ghost cells after the last group
            if (colsUsed > 0 && colsUsed < COLS) {
              for (let f = 0; f < COLS - colsUsed; f++) {
                rows.push(<div key={`fill-end-${f}`} style={{ background: BG2 }} />);
              }
            }

            return rows;
          })()}
        </div>
      </section>

      {/* ── FOUNDER STORY ──────────────────────────────────────────────────────── */}
      <section className="hp-fade" style={{
        borderTop: `1px solid ${BDR2}`,
        borderBottom: `1px solid ${BDR2}`,
        padding: '120px 40px',
        background: BG,
        position: 'relative', zIndex: 1,
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
          <div>
            <SectionLabel text="Why This Exists" />
            <h2 style={{
              fontFamily: DISPLAY,
              fontSize: 'clamp(2rem, 3.5vw, 3rem)',
              fontWeight: 700,
              lineHeight: 1.15,
              color: '#ffffff',
              marginBottom: 32,
              margin: '0 0 32px',
            }}>
              I was learning to trade. I kept hitting the same wall.
            </h2>
            <div style={{ width: 48, height: 2, background: GREEN, marginBottom: 32 }} />
            <p style={{ fontFamily: SANS, fontSize: '1rem', color: MUTED, lineHeight: 1.75, marginBottom: 16 }}>
              The tools that answered my questions cost $10,000–$24,000 a year. Built for institutions. Built for people who had already been let inside.
            </p>
            <p style={{ fontFamily: SANS, fontSize: '1rem', color: MUTED, lineHeight: 1.75, marginBottom: 32 }}>
              I couldn't find one place with everything I needed. So I built it.
            </p>
            <a href="/about" style={{
              fontFamily: SANS,
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#ffffff',
              textDecoration: 'none',
              borderBottom: '1px solid #333',
              paddingBottom: 2,
            }}>
              Read the full story →
            </a>
          </div>
          <div style={{
            background: GDARK,
            border: `1px solid ${BDR2}`,
            padding: 40,
          }}>
            <p style={{
              fontFamily: DISPLAY,
              fontSize: '1.25rem',
              fontStyle: 'italic',
              color: '#ffffff',
              lineHeight: 1.6,
              marginBottom: 32,
            }}>
              "Trading is not gambling. It never was. Nobody just told the 90%."
            </p>
            <div style={{ borderTop: `1px solid ${BDR2}`, paddingTop: 24 }}>
              <p style={{ fontFamily: SANS, fontSize: '0.875rem', fontWeight: 600, color: '#ffffff', marginBottom: 4 }}>
                Founder, Nuvport OS
              </p>
              <p style={{ fontFamily: SANS, fontSize: '0.8125rem', color: MUTED }}>
                19 years old · Global Business Student · Zagreb, Croatia
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ──────────────────────────────────────────────────────── */}
      <section id="hp-pricing" className="hp-fade" style={{ background: BG2, borderTop: `1px solid ${BDR2}`, borderBottom: `1px solid ${BDR2}`, position: "relative", zIndex: 1, padding: "80px 40px" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <SectionLabel text="Pricing" />
          <h2 style={{ fontFamily: DISPLAY, fontSize: "clamp(28px,5vw,52px)", fontWeight: 400, letterSpacing: ".03em", color: "#fff", lineHeight: 1.1, margin: 0 }}>
            Simple pricing.
          </h2>
        </div>

        {/* Two-column table */}
        <div className="hp-plans-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, maxWidth: 860, margin: "0 auto 32px" }}>

          {/* FREE */}
          <div style={{ background: BG, border: `1px solid ${BDR2}`, padding: "40px 36px", position: "relative" }}>
            <p style={{ fontSize: 9, letterSpacing: ".2em", textTransform: "uppercase", color: MUTED, marginBottom: 12 }}>Free</p>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 6 }}>
              <span style={{ fontFamily: DISPLAY, fontSize: 13, color: MUTED, alignSelf: "flex-start", paddingTop: 8, letterSpacing: ".1em" }}>$</span>
              <span style={{ fontFamily: DISPLAY, fontSize: 64, lineHeight: 1, color: "#fff", letterSpacing: ".02em" }}>0</span>
              <span style={{ fontFamily: MONO, fontSize: 10, color: MUTED, letterSpacing: ".08em" }}>/ forever</span>
            </div>
            <p style={{ fontFamily: SANS, fontSize: 12, color: MUTED, lineHeight: 1.8, fontWeight: 300, marginBottom: 28 }}>
              Get started with the core portfolio tools. No credit card required.
            </p>
            <div style={{ height: 1, background: BDR2, marginBottom: 24 }} />
            <div style={{ marginBottom: 32 }}>
              {[
                "Up to 5 holdings",
                "Dashboard — full portfolio overview",
                "Builder — build and weight your portfolio",
                "Watchlist — live prices and sparklines",
                "No credit card required",
              ].map(feat => (
                <div key={feat} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <span style={{ width: 4, height: 4, borderRadius: "50%", background: MUTED, flexShrink: 0 }} />
                  <span style={{ fontFamily: SANS, fontSize: 12, color: MUTED, fontWeight: 300 }}>{feat}</span>
                </div>
              ))}
            </div>
            <button
              className="hp-btn-ghost"
              onClick={onLogin}
              onMouseEnter={makeBig} onMouseLeave={makeSmall}
            >
              Start Free →
            </button>
          </div>

          {/* PRO */}
          <div style={{ background: BG, border: `1px solid ${GREEN}`, padding: "40px 36px", position: "relative", boxShadow: `0 0 32px rgba(61,220,132,.08)` }}>
            {/* Green top bar */}
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${GDARK},${GREEN})` }} />
            {/* Most Popular badge */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(61,220,132,.1)", border: `1px solid ${GREEN}44`, padding: "3px 12px", marginBottom: 20, fontSize: 8, letterSpacing: ".16em", textTransform: "uppercase", color: GREEN, fontFamily: MONO }}>
              <span className="hp-blink" style={{ width: 4, height: 4, borderRadius: "50%", background: GREEN, display: "inline-block" }} />
              MOST POPULAR
            </div>
            <p style={{ fontSize: 9, letterSpacing: ".2em", textTransform: "uppercase", color: MUTED, marginBottom: 12 }}>Pro</p>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 6 }}>
              <span style={{ fontFamily: DISPLAY, fontSize: 13, color: MUTED, alignSelf: "flex-start", paddingTop: 8, letterSpacing: ".1em" }}>$</span>
              <span style={{ fontFamily: DISPLAY, fontSize: 64, lineHeight: 1, color: "#ffffff", letterSpacing: ".02em" }}>29</span>
              <span style={{ fontFamily: MONO, fontSize: 10, color: MUTED, letterSpacing: ".08em" }}>/ month</span>
            </div>
            <p style={{ fontFamily: SANS, fontSize: 12, color: MUTED, lineHeight: 1.8, fontWeight: 300, marginBottom: 28 }}>
              Full access to all 22 modules, geo-risk intelligence, insider filings, earnings data, and unlimited holdings.
            </p>
            <div style={{ height: 1, background: `${GREEN}33`, marginBottom: 24 }} />
            <div style={{ marginBottom: 32 }}>
              <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: ".14em", color: MUTED, textTransform: "uppercase", marginBottom: 12 }}>Everything in Free, plus:</div>
              {[
                "Unlimited holdings",
                "All 22 modules unlocked",
                "Valuation and Screener",
                "Optimizer — efficient frontier",
                "DCF Model — intrinsic value calculator",
                "Monte Carlo — 10-year simulations",
                "Advanced Analytics — 8-chart dashboard",
                "Backtest — historical vs S&P 500",
                "Geo-Risk Map — 40+ layers, 19 zones",
                "FED Monitor and Macro Command Center",
                "Econ Statistics — 6 country comparison",
                "Earnings calendar and EPS estimates",
                "Insider Intel — 500+ Form 4 filings",
                "News Sentiment — AI-scored, 7 feeds",
                "CSV export",
              ].map(feat => (
                <div key={feat} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#ffffff", flexShrink: 0 }} />
                  <span style={{ fontFamily: SANS, fontSize: 12, color: TEXT, fontWeight: 300 }}>{feat}</span>
                </div>
              ))}
            </div>
            <button
              className="hp-btn-primary"
              onClick={onLogin}
              onMouseEnter={makeBig} onMouseLeave={makeSmall}
              style={{ width: "100%", padding: "13px 0", clipPath: "polygon(0 0,calc(100% - 8px) 0,100% 8px,100% 100%,8px 100%,0 calc(100% - 8px))" }}
            >
              Get Pro →
            </button>
          </div>
        </div>

        {/* Reassurance line */}
        <div style={{ textAlign: "center", fontFamily: SANS, fontSize: 12, color: MUTED, fontWeight: 300, letterSpacing: ".04em" }}>
          No lock-in. Cancel anytime. Upgrade or downgrade instantly.
        </div>
      </section>

      {/* ── WAITLIST / BETA ACCESS ───────────────────────────────────────── */}
      <section style={{ background: BG, borderTop: `1px solid ${BDR2}`, position: "relative", zIndex: 1, padding: "120px 40px", textAlign: "center", overflow: "hidden" }}>
        {/* Pulsing rings */}
        <div style={{ position: "absolute", top: "50%", left: "50%", pointerEvents: "none" }}>
          {[320, 560, 800, 1040].map((size, i) => (
            <div key={size} style={{
              position: "absolute", borderRadius: "50%",
              border: `1px solid rgba(61,220,132,${0.06 - i * 0.01})`,
              width: size, height: size,
              transform: "translate(-50%,-50%)",
              animation: `hp-ringPulse 4s ease ${i * 0.7}s infinite`,
            }} />
          ))}
        </div>
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 400, background: "radial-gradient(ellipse,rgba(61,220,132,.05) 0%,transparent 70%)", pointerEvents: "none" }} />

        <div style={{ position: "relative", zIndex: 2, maxWidth: 560, margin: "0 auto" }}>
          <SectionLabel text="Private Beta" />

          <h2 style={{ fontFamily: DISPLAY, fontSize: "clamp(28px,5vw,60px)", fontWeight: 400, letterSpacing: ".03em", lineHeight: 1.08, color: "#fff", margin: "0 0 16px" }}>
            Institutional intelligence.<br />
            <span style={{ color: GREEN }}>Early access.</span>
          </h2>

          {/* Live counter */}
          <div style={{ fontFamily: MONO, fontSize: 11, color: MUTED, letterSpacing: ".1em", marginBottom: 40 }}>
            {wlCount != null
              ? <><span style={{ color: GREEN, fontWeight: 700 }}>{wlCount.toLocaleString()}</span> people waiting for access</>
              : "Nuvport OS is currently in private beta"}
          </div>

          {/* Dual CTAs */}
          <div className="hp-hero-ctas" style={{ justifyContent: "center", marginBottom: 32 }}>
            <button
              className="hp-btn-primary"
              onClick={onLogin}
              onMouseEnter={makeBig}
              onMouseLeave={makeSmall}
            >
              I have a beta code
            </button>
            <button
              className="hp-btn-secondary"
              onClick={() => setWaitlistOpen(v => !v)}
              onMouseEnter={makeBig}
              onMouseLeave={makeSmall}
            >
              {waitlistOpen ? "Close" : "Join the waitlist"}
            </button>
          </div>

          {/* ── Inline waitlist form ─────────────────────────────────────── */}
          {waitlistOpen && (
            <div style={{
              background: BG2,
              border: `1px solid ${BORDER}`,
              borderTop: `2px solid ${GREEN}`,
              borderRadius: 12,
              padding: "28px 32px",
              textAlign: "left",
              marginBottom: 24,
            }}>
              {wlStatus === "success" ? (
                <div style={{ textAlign: "center", padding: "8px 0" }}>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: GREEN, letterSpacing: ".14em", marginBottom: 10 }}>
                    ✓ YOU'RE ON THE LIST
                  </div>
                  <div style={{ fontFamily: DISPLAY, fontSize: 22, color: TEXT, marginBottom: 16 }}>
                    You're on the list. We'll be in touch.
                  </div>
                  {myReferralCode && (
                    <div style={{ marginTop: 20 }}>
                      <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED, letterSpacing: ".12em", marginBottom: 8 }}>
                        YOUR REFERRAL LINK — share it to move up the list
                      </div>
                      <div
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/?ref=${myReferralCode}`);
                        }}
                        title="Click to copy"
                        style={{
                          fontFamily: MONO, fontSize: 12, color: GREEN,
                          background: BG3, border: `1px solid ${BORDER}`,
                          borderRadius: 6, padding: "10px 14px",
                          cursor: "pointer", letterSpacing: ".06em",
                          wordBreak: "break-all",
                        }}
                      >
                        {window.location.origin}/?ref={myReferralCode}
                        <span style={{ color: MUTED, marginLeft: 10, fontSize: 10 }}>· click to copy</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : wlStatus === "duplicate" ? (
                <div style={{ textAlign: "center", padding: "8px 0" }}>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: GREEN, letterSpacing: ".14em", marginBottom: 8 }}>
                    ALREADY REGISTERED
                  </div>
                  <div style={{ fontFamily: SANS, fontSize: 14, color: MUTED }}>
                    That email is already on the waitlist. We'll reach out when access opens.
                  </div>
                </div>
              ) : (
                <form onSubmit={handleWaitlistSubmit}>
                  <div style={{ fontFamily: MONO, fontSize: 9, color: MUTED, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 18 }}>
                    Join the waitlist
                  </div>

                  {/* Email */}
                  <div style={{ marginBottom: 14 }}>
                    <input
                      type="email"
                      required
                      placeholder="your@email.com"
                      value={wlEmail}
                      onChange={e => { setWlEmail(e.target.value); setWlStatus("idle"); }}
                      style={{
                        width: "100%", boxSizing: "border-box",
                        background: BG3, border: `1px solid ${BORDER}`,
                        borderRadius: 8, padding: "11px 14px",
                        fontFamily: MONO, fontSize: 13, color: TEXT,
                        outline: "none", transition: "border-color .15s",
                      }}
                      onFocus={e => { e.currentTarget.style.borderColor = GREEN; }}
                      onBlur={e => { e.currentTarget.style.borderColor = BORDER; }}
                    />
                  </div>

                  {/* Interest reason */}
                  <div style={{ marginBottom: 18 }}>
                    <select
                      required
                      value={wlReason}
                      onChange={e => setWlReason(e.target.value)}
                      style={{
                        width: "100%", boxSizing: "border-box",
                        background: BG3, border: `1px solid ${BORDER}`,
                        borderRadius: 8, padding: "11px 14px",
                        fontFamily: MONO, fontSize: 12, color: wlReason ? TEXT : MUTED,
                        outline: "none", cursor: "pointer",
                        appearance: "none",
                      }}
                    >
                      <option value="" disabled>Why are you interested?</option>
                      {INTEREST_OPTIONS.map(opt => (
                        <option key={opt} value={opt} style={{ background: BG2, color: TEXT }}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  {wlErrMsg && (
                    <div style={{ fontFamily: MONO, fontSize: 10, color: RED, marginBottom: 12, letterSpacing: ".04em" }}>
                      {wlErrMsg}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={!wlEmail.trim() || !wlReason || wlStatus === "loading"}
                    className={wlEmail.trim() && wlReason ? "hp-btn-primary" : ""}
                    style={{
                      width: "100%",
                      background: wlEmail.trim() && wlReason ? GREEN : "#1c2333",
                      border: "none",
                      borderRadius: 8,
                      padding: "13px 0",
                      fontFamily: MONO, fontSize: 11, fontWeight: 700,
                      color: wlEmail.trim() && wlReason ? BG : MUTED,
                      letterSpacing: ".1em",
                      cursor: wlEmail.trim() && wlReason ? "pointer" : "default",
                      transition: "all .15s",
                      clipPath: wlEmail.trim() && wlReason
                        ? "polygon(0 0,calc(100% - 10px) 0,100% 10px,100% 100%,10px 100%,0 calc(100% - 10px))"
                        : "none",
                    }}
                  >
                    {wlStatus === "loading" ? "JOINING..." : "JOIN WAITLIST →"}
                  </button>
                </form>
              )}
            </div>
          )}

          <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED, letterSpacing: ".08em", lineHeight: 2 }}>
            No credit card required.&nbsp;&nbsp;·&nbsp;&nbsp;We'll notify you when access opens.
          </div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────────── */}
      <footer style={{ borderTop: `1px solid ${BDR2}`, background: BG2, position: "relative", zIndex: 1 }}>
        {/* Main footer row */}
        <div className="hp-footer-main" style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "start", gap: 40, padding: "40px 48px", borderBottom: `1px solid ${BDR2}` }}>
          {/* Left — brand */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <svg width="28" height="28" viewBox="0 0 52 52" fill="none" style={{ flexShrink: 0 }}>
                <rect width="52" height="52" rx="13" fill="#0d1520"/>
                <rect x="12" y="10" width="6" height="32" rx="3" fill="#3ddc84"/>
                <rect x="34" y="10" width="6" height="32" rx="3" fill="#3ddc84" opacity="0.28"/>
                <line x1="15" y1="11.5" x2="37" y2="40.5" stroke="#3ddc84" strokeWidth="6" strokeLinecap="round"/>
              </svg>
              <div>
                <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 18, letterSpacing: ".08em", color: "#fff", lineHeight: 1 }}>NUVPORT</div>
                <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: ".3em", color: GREEN, lineHeight: 1, marginTop: 2 }}>OS</div>
              </div>
            </div>
            <div style={{ fontFamily: MONO, fontSize: 9, color: MUTED, letterSpacing: ".14em", textTransform: "uppercase" }}>
              Bloomberg intelligence. Not Bloomberg prices.
            </div>
          </div>

          {/* Center — nav links */}
          <div style={{ display: "flex", gap: 32, alignItems: "center" }}>
            {[
              { label: "About",    href: "/about" },
              { label: "Pricing",  href: "/pricing" },
              { label: "FAQ",      href: "/faq" },
            ].map(l => (
              <a key={l.label} className="hp-fl-link" href={l.href}
                style={{ fontFamily: MONO, fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase" }}>
                {l.label}
              </a>
            ))}
          </div>

          {/* Right — legal links */}
          <div style={{ display: "flex", gap: 24, alignItems: "center", justifyContent: "flex-end" }}>
            <button onClick={onOpenTerms} className="hp-fl-link"
              style={{ background: "none", border: "none", padding: 0, cursor: "none", fontFamily: MONO, fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", color: MUTED }}>
              Terms of Service
            </button>
            <button onClick={onOpenPrivacy} className="hp-fl-link"
              style={{ background: "none", border: "none", padding: 0, cursor: "none", fontFamily: MONO, fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", color: MUTED }}>
              Privacy Policy
            </button>
            <button onClick={() => { try { localStorage.removeItem("cookie_notice_accepted"); } catch {} window.location.reload(); }} className="hp-fl-link"
              style={{ background: "none", border: "none", padding: 0, cursor: "none", fontFamily: MONO, fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", color: MUTED }}>
              Cookie Settings
            </button>
          </div>
        </div>

        {/* Bottom bar — disclaimer + copyright */}
        <div className="hp-footer-bottom" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, padding: "16px 48px" }}>
          <div style={{ fontFamily: MONO, fontSize: 9, color: MUTED, letterSpacing: ".06em", opacity: 0.7, maxWidth: 600, lineHeight: 1.7 }}>
            Not financial advice. Nuvport OS is an information platform only. Nothing on this site constitutes investment advice,
            a recommendation to buy or sell any security, or an offer to provide investment advisory services.
            Past performance is not indicative of future results. Investing involves risk including loss of principal.
          </div>
          <div style={{ fontFamily: MONO, fontSize: 9, color: MUTED, letterSpacing: ".08em", opacity: 0.5, flexShrink: 0 }}>
            © {new Date().getFullYear()} Nuvport OS. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
